import { UserTypes } from "../contexts/UserContext";
import { dbService } from "../firebaseInstance";
import { arrayUnion } from "firebase/firestore";
import { uploadAndMakePublicAvatar } from './publicAvatarUtils';
import { saveTokens } from './tokenManager';
import { getActiveToken, handleTokenError } from './getActiveToken';
import { graphGet } from './graphProxy';
import { firebaseInstance } from '../firebaseInstance';

// Api Call for user profile
export const getProfile = async () => {
	const token = getActiveToken();

	// 토큰이 없는 경우 에러 처리
	if (!token) {
		throw new Error("No access token found");
	}

	try {
		// graph.threads.net 은 브라우저 CORS 미지원 → threadsApiProxy CF 경유
		return await graphGet('me', {
			params: {
				fields:
					"id,username,name,threads_profile_picture_url,threads_biography",
				access_token: token,
			},
		}, { retryOn5xx: false });
	} catch (error) {
		// 토큰 관련 에러인 경우 로컬 스토리지 정리
		handleTokenError(error);
		throw error;
	}
};

// Fetching & Setting user data used globally as state
export const fetchUserData = async (dispatch) => {
	try {
		const profileResponse = await getProfile();
		const profileData = profileResponse.data;

		dispatch({
			type: UserTypes.SET_PROFILE,
			payload: {
				profile: profileData,
				accessToken: getActiveToken(),
			},
		});

		// 🆕 아바타 Public URL 업로드 (최초 로그인 시에만)
		if (profileData.id && profileData.threads_profile_picture_url) {
			console.log('🚀 [LOGIN] Starting avatar upload process...');
			console.log('🚀 [LOGIN] User ID:', profileData.id);
			console.log('🚀 [LOGIN] Profile image URL:', profileData.threads_profile_picture_url);
			
			// 백그라운드에서 Public URL 업로드 (실패해도 로그인 진행)
			uploadAndMakePublicAvatar(profileData.id, profileData.threads_profile_picture_url)
				.then(publicUrl => {
					console.log('🎉 [LOGIN SUCCESS] Avatar ready at public URL:', publicUrl);
				})
				.catch(error => {
					console.warn('⚠️ [LOGIN] Avatar upload failed (non-critical):', error.message);
					// 실패해도 로그인 프로세스는 계속 진행
				});

			// 🆕 토큰 정보 Firestore에 저장 (기존 호환성 유지)
			const accessToken = getActiveToken();
			const longLivedToken = localStorage.getItem("longLivedToken");
			
			if (accessToken && profileData.id) {
				saveTokens(profileData.id, accessToken, longLivedToken || accessToken)
					.catch(error => {
						console.warn('⚠️ Token saving failed:', error.message);
						// 실패해도 로그인 프로세스는 계속 진행
					});
			}
		}

	} catch (error) {
		dispatch({
			type: UserTypes.CLEAR_PROFILE,
		});
		
		// 토큰 관련 에러인 경우 상세 로그 제공
		if (error.message === "No access token found") {
			console.warn("User not authenticated - redirecting to login");
		} else if (error.response?.status === 400) {
			console.warn("Invalid or expired access token - clearing authentication");
		} else {
			console.error("Error fetching user data:", error);
		}
	}
};

// Collection Management Functions
export const createCollection = async (data) => {
	try {
		const collectionRef = dbService.collection("collection");
		const docRef = await collectionRef.add(data);

		const userRef = dbService.collection("user").doc(data.userID);
		await userRef.update({
			collectionList: arrayUnion(docRef.id),
		});

		return { id: docRef.id, ...data };
	} catch (error) {
		console.error("Error adding document: ", error);
		throw error;
	}
};

export const getMyCollections = async (userID) => {
	try {
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();
	
		if (userDoc.exists) {
			const userData = userDoc.data();
				const collectionList = userData.collectionList || [];
			const collections = await Promise.all(
				collectionList.map(async (collectionId) => {
					const collectionRef = dbService
						.collection("collection")
						.doc(collectionId);
					const collectionDoc = await collectionRef.get();
					if (collectionDoc.exists) {
						const collectionData = collectionDoc.data();
						const threadList = collectionData.threadList || [];

						const metric = {
							replies: 0,
							reposts: 0,
							likes: 0,
							views: 0,
						};

						threadList.forEach((thread) => {
							metric.replies += thread.replies || 0;
							metric.reposts += thread.reposts || 0;
							metric.likes += thread.likes || 0;
							metric.views += thread.views || 0;
						});

						collectionData.metric = metric;

						return { id: collectionId, ...collectionData };
					} else {
						console.error(
							`No such collection document with ID: ${collectionId}`
						);
						return null;
					}
				})
			);

			const filteredCollections = collections.filter((collection) => collection !== null);
			return filteredCollections;
		} else {
			console.error("No such user document!");
			return [];
		}
	} catch (error) {
		console.error("Error fetching collections: ", error);
		throw error;
	}
};

export const getCollection = async (collectionId) => {
	const collectionRef = dbService.collection("collection").doc(collectionId);
	const collectionDoc = await collectionRef.get();
	return collectionDoc.data();
};

export const updateCollection = async (collectionID, data) => {
	try {
		const collectionRef = dbService.collection("collection").doc(collectionID);
		await collectionRef.update(data);

		return { id: collectionID, ...data };
	} catch (error) {
		console.error("Error updating document: ", error);
		throw error;
	}
};

export const deleteCollection = async (collectionID) => {
	const collectionRef = dbService.collection("collection").doc(collectionID);
	await collectionRef.delete();
};

export const getAllCollection = async () => {
	try {
		const collectionRef = dbService.collection("collection");
		const snapshot = await collectionRef.get();
		const collections = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return collections;
	} catch (error) {
		console.error("Error fetching all collections: ", error);
		throw error;
	}
};

// Thread Replies
export const getReplies = async (threadId) => {
	const token = getActiveToken();
	// graph.threads.net 은 브라우저 CORS 미지원 → threadsApiProxy CF 경유
	const response = await graphGet(`${threadId}/replies`, {
		params: {
			fields: "id,text,username,user_id",
			reverse: false,
			access_token: token,
		},
	}, { retryOn5xx: false });
	return response.data.data;
};

// Get all replies with pagination
export const getAllReplies = async (threadId) => {
	const token = getActiveToken();
	let allReplies = [];
	// 첫 페이지는 상대 경로, 이후 페이지는 paging.next 절대 URL
	let url = `${threadId}/replies`;

	try {
		while (url) {
			// graph.threads.net 은 브라우저 CORS 미지원 → threadsApiProxy CF 경유
			const response = await graphGet(url, {
				params: {
					fields: "id,text,username,user_id",
					reverse: false,
					access_token: token,
				},
			}, { retryOn5xx: false });

			const data = response.data;
			if (data.data && data.data.length > 0) {
				allReplies = allReplies.concat(data.data);
			}

			// Check for next page
			url = data.paging && data.paging.next ? data.paging.next : null;
		}

		return allReplies;
	} catch (error) {
		console.error(`Error fetching all replies for thread ${threadId}:`, error);
		throw error;
	}
};

// Admin Functions
export const getUserInsight = async (userID) => {
	const userRef = dbService.collection("user").doc(userID);
	const userDoc = await userRef.get();
	return userDoc.data();
};

// 모든 유저 정보 가져오기 (ThreaderList용)
export const getAllUsers = async () => {
	try {
		const userDocRef = dbService.collection("user");
		const userDoc = await userDocRef.get();
		const usersData = [];

		for (const doc of userDoc.docs) {
			const userData = doc.data();
			const userInsightDoc = await doc.ref
				.collection("insight")
				.doc("insight")
				.get();
			const userInsight = userInsightDoc.exists ? userInsightDoc.data() : {};
			
			// 프로필이 있는 유저만 포함
			if (userData.profile) {
				usersData.push({ 
					id: doc.id,
					...userData, 
					insight: userInsight 
				});
			}
		}

		return usersData;
	} catch (error) {
		console.error("Error fetching all users: ", error);
		throw error;
	}
}; 

// Threads People Directory APIs
export const getThreadsPeople = async ({ limit = 24, activeOnly = true, q = '' } = {}) => {
  try {
    let query = dbService.collection('threadsPeople');
    if (activeOnly) {
      query = query.where('isActive', '==', true);
    }
    if (limit && typeof query.limit === 'function') {
      query = query.limit(limit);
    }
    const snapshot = await query.get();
    let list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    if (q && typeof q === 'string' && q.trim().length > 0) {
      const needle = q.trim().toLowerCase();
      list = list.filter((p) => {
        const u = (p.username || '').toLowerCase();
        const n = (p.name || '').toLowerCase();
        return u.includes(needle) || n.includes(needle);
      });
    }
    return list;
  } catch (error) {
    console.error('Error getThreadsPeople:', error);
    throw error;
  }
};

export const getThreadsPeopleByIds = async (userIds = []) => {
  try {
    if (!Array.isArray(userIds) || userIds.length === 0) return {};
    const uniqueIds = Array.from(new Set(userIds.map((v) => String(v))));
    const snaps = await Promise.all(
      uniqueIds.map((id) => dbService.collection('threadsPeople').doc(id).get())
    );
    const map = {};
    snaps.forEach((snap, idx) => {
      const id = uniqueIds[idx];
      if (snap.exists) map[id] = { id, ...snap.data() };
    });
    return map;
  } catch (error) {
    console.error('Error getThreadsPeopleByIds:', error);
    throw error;
  }
};

export const upsertThreadsPerson = async (userId, { bio, businessLink, snapshot = {}, categoryKeys, categoryLabels } = {}) => {
  try {
    if (!userId) throw new Error('userId is required');
    const id = String(userId);
    const ref = dbService.collection('threadsPeople').doc(id);
    const now = new Date();
    const doc = await ref.get();

    const base = {};
    base.userId = id;
    if (typeof snapshot.username === 'string') base.username = snapshot.username;
    if (typeof snapshot.name === 'string') base.name = snapshot.name;
    if (typeof snapshot.profileImage === 'string') base.profileImage = snapshot.profileImage;
    if (typeof bio === 'string') base.bio = bio;
    if (typeof businessLink === 'string') base.businessLink = businessLink;
    if (Array.isArray(categoryKeys)) base.categoryKeys = categoryKeys;
    if (Array.isArray(categoryLabels)) base.categoryLabels = categoryLabels;

    if (doc.exists) {
      await ref.update({
        ...base,
        updatedAt: now,
      });
    } else {
      await ref.set({
        ...base,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      });
    }
    const saved = await ref.get();
    return { id, ...saved.data() };
  } catch (error) {
    console.error('Error upsertThreadsPerson:', error);
    throw error;
  }
};

export const setThreadsPersonActive = async (userId, isActive) => {
  try {
    if (!userId) throw new Error('userId is required');
    const id = String(userId);
    const ref = dbService.collection('threadsPeople').doc(id);
    await ref.update({ isActive: Boolean(isActive), updatedAt: new Date() });
  } catch (error) {
    console.error('Error setThreadsPersonActive:', error);
    throw error;
  }
};